import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { HomeCta, HomeIntegrationCard, HomeIntegrationService, HomeIntegrationsData } from "@/lib/homepage";

type IntegrationsProps = {
  data: HomeIntegrationsData;
};

function ServiceBadge({ service }: { service: HomeIntegrationService }) {
  return (
    <div
      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-sm"
      style={{
        backgroundColor: service.backgroundRgba || "rgba(151, 71, 255, 0.15)",
        color: service.colorHex || "#ffffff",
      }}
    >
      {service.icon ? <span className="onplexus-icon text-3xl pt-0.5">{service.icon}</span> : null}
      <span className="font-normal w-auto flex-grow-0">{service.label}</span>
    </div>
  );
}

function IntegrationCard({ card }: { card: HomeIntegrationCard }) {
  return (
    <div className="flex-shrink-0 w-64 p-px rounded-xl border border-white/8 bg-gradient-to-b from-white/3 to-transparent hover:border-white/15 transition-all duration-300">
      <div className="p-4 flex flex-col items-center gap-4 overflow-x-hidden">
        {/* Title */}
        <h3 className="text-base font-bold text-white/70 w-full">
          {card.name}
        </h3>
        
        {/* Logo */}
        <div className="flex items-center justify-center w-24 h-24">
          {card.logo ? (
            <img
              src={card.logo}
              alt={card.name ? `${card.name} logo` : "Integration logo"}
              className="max-w-full max-h-full object-contain"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="h-20 w-20 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 text-xs">
              Logo coming soon
            </div>
          )}
        </div>
        
        {/* Service Badges */}
        <div className="flex gap-2 justify-start flex-row items-center w-full">
      {card.services.map((service, index) => (
        <ServiceBadge key={index} service={service} />
          ))}
        </div>
      </div>
    </div>
  );
}

function AllIntegrationsButton({ cta }: { cta?: HomeCta | null }) {
  if (!cta) return null;
  const isExternal = /^(https?:)?\/\//i.test(cta.url) || cta.url.startsWith("mailto:") || cta.url.startsWith("tel:");
  if (isExternal) {
    return (
      <Button
        asChild
        variant="outline"
        className="border-onplexus-gold text-onplexus-gold hover:bg-onplexus-gold hover:text-onplexus-dark bg-transparent rounded-xl px-6 py-3 h-12 font-semibold text-lg"
      >
        <a href={cta.url}>{cta.label}</a>
      </Button>
    );
  }
  return (
    <Button
      asChild
      variant="outline"
      className="border-onplexus-gold text-onplexus-gold hover:bg-onplexus-gold hover:text-onplexus-dark bg-transparent rounded-xl px-6 py-3 h-12 font-semibold text-lg"
    >
      <Link to={cta.url}>{cta.label}</Link>
    </Button>
  );
}

export default function Integrations({ data }: IntegrationsProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  // Initialize with mobile default to ensure SSR/client match
  const [cardsPerView, setCardsPerView] = useState(1);

  // Calculate how many cards to show based on screen size
  const getCardsPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1024) return 4; // lg
      if (window.innerWidth >= 768) return 3;  // md
      if (window.innerWidth >= 640) return 2;  // sm
    }
    return 1; // mobile
  };

  // Handle responsive behavior
  useEffect(() => {
    const updateCardsPerView = () => {
      const newCardsPerView = getCardsPerView();
      setCardsPerView(newCardsPerView);
      // Reset index if it exceeds new max
      const newMaxIndex = Math.max(0, data.cards.length - newCardsPerView);
      setCurrentIndex(prev => Math.min(prev, newMaxIndex));
    };

    // Set initial value
    updateCardsPerView();

    // Add resize listener
    window.addEventListener('resize', updateCardsPerView);

    return () => window.removeEventListener('resize', updateCardsPerView);
  }, [data.cards.length]);

  const maxIndex = Math.max(0, data.cards.length - cardsPerView);
  
  const goToPrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  if (!data.cards.length) return null;

  return (
    <section className="py-12 intergations-section">
      <div className="max-w-6xl mx-auto">
        <div className="space-y-12">
          {/* Section Header */}
          <div className="text-center space-y-6">
            <div className="flex items-center justify-center gap-3">
              <div className="h-0.5 w-24 bg-gradient-to-r from-transparent via-onplexus-gold-bright to-onplexus-gold"></div>
              <span className="title-mini">
                {data.kicker}
              </span>
              <div className="h-0.5 w-24 bg-gradient-to-l from-transparent via-onplexus-gold-bright to-onplexus-gold"></div>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white leading-7">
                {data.heading}
              </h2>
              <p className="text-2xl text-white/70 leading-7">
                {data.subheading}
              </p>
            </div>
          </div>

          {cardsPerView > 1 && (
            <div className="flex justify-center gap-2 integrations-carousel-controls">
              <button
                onClick={goToPrevious}
                disabled={currentIndex === 0}
                className={`flex items-center justify-center w-11 h-11 rounded-xl transition-colors ${
                  currentIndex === 0 ? "text-white/60 cursor-not-allowed" : "text-white hover:bg-white/5"
                }`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={goToNext}
                disabled={currentIndex >= maxIndex}
                className={`flex items-center justify-center w-11 h-11 rounded-xl transition-colors ${
                  currentIndex >= maxIndex ? "text-white/60 cursor-not-allowed" : "text-white hover:bg-white/5"
                }`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}

          <div className="relative overflow-hidden">
            {cardsPerView === 1 ? (
              <div className="flex flex-col items-center gap-6">
                {data.cards.map((card) => (
                  <IntegrationCard key={card.id} card={card} />
                ))}
              </div>
            ) : (
              <div
                className="flex gap-6 transition-transform duration-500 ease-in-out integrations-carousel-card-container"
                style={{ transform: `translateX(-${currentIndex * (256 + 24)}px)` }}
              >
                {data.cards.map((card) => (
                  <IntegrationCard key={card.id} card={card} />
                ))}
              </div>
            )}
          </div>

          {/* CTA Button */}
          <div className="flex justify-center">
            <AllIntegrationsButton cta={data.cta} />
          </div>
        </div>
      </div>
    </section>
  );
}
